import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { dateToIsoUtc, isoToDate } from "../../../utils/rfidExpiry";

interface RfidFormDialogProps {
    visible: boolean;
    onHide: () => void;
    onSubmit: (uid: string, cardCode: string, validUntil: string | null) => Promise<void> | void;
    mode: "create" | "update";
    initialUid?: string;
    initialCardCode?: string;
    initialValidUntil?: string | null;
    /** When true, only Valid Until is editable; the card code is shown read-only. */
    lockCardCode?: boolean;
}

export function RfidFormDialog({
    visible,
    onHide,
    onSubmit,
    mode,
    initialUid = "",
    initialCardCode = "",
    initialValidUntil = null,
    lockCardCode = false
}: RfidFormDialogProps) {
    const [uid, setUid] = useState<string>("");
    const [cardCode, setCardCode] = useState<string>("");
    const [validUntil, setValidUntil] = useState<Date | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (visible) {
            setUid(initialUid);
            setCardCode(initialCardCode);
            setValidUntil(isoToDate(initialValidUntil));
        } else {
            setUid("");
            setCardCode("");
            setValidUntil(null);
        }
    }, [visible, initialUid, initialCardCode, initialValidUntil]);

    const isCreate = mode === "create";
    const header = isCreate ? "Add RFID Card" : "Update RFID Card";
    const submitLabel = isCreate ? "Add RFID card" : "Update";
    const submitIcon = isCreate ? "pi pi-link" : "pi pi-check";

    const footer = (
        <div>
            <Button label="Cancel" icon="pi pi-times" onClick={onHide} className="p-button-text" />
            <Button
                label={submitLabel}
                icon={submitIcon}
                onClick={async () => {
                    setLoading(true);
                    try {
                        await onSubmit(uid, cardCode, dateToIsoUtc(validUntil));
                    } finally {
                        setLoading(false);
                    }
                }}
                loading={loading}
                autoFocus
            />
        </div>
    );

    return (
        <Dialog
            header={header}
            visible={visible}
            style={{ width: "30rem" }}
            onHide={onHide}
            footer={footer}
        >
            <div className="field mb-4">
                <label htmlFor="uid" className="block mb-2 text-lg font-medium">Card Uid</label>
                {isCreate ? (
                    <InputText
                        id="uid"
                        className="block w-full"
                        value={uid}
                        onChange={(e) => setUid(e.target.value)}
                        placeholder="Enter card uid"
                    />
                ) : (
                    <div id="uid" className="text-lg p-2 bg-gray-100 border-round">{uid}</div>
                )}
            </div>

            <div className="field mb-4">
                <label htmlFor="cardCode" className="block mb-2 text-lg font-medium">
                    Card Code
                </label>
                {lockCardCode ? (
                    <div id="cardCode" className="text-lg p-2 bg-gray-100 border-round">{cardCode}</div>
                ) : (
                    <InputText
                        id="cardCode"
                        className="block w-full"
                        value={cardCode}
                        onChange={(e) => setCardCode(e.target.value)}
                        placeholder={isCreate ? "Enter printed card code" : "Enter new card code"}
                    />
                )}
            </div>

            <div className="field">
                <label htmlFor="validUntil" className="block mb-2 text-lg font-medium">
                    Valid Until <span className="text-sm text-color-secondary font-normal">(optional)</span>
                </label>
                <Calendar
                    inputId="validUntil"
                    className="w-full"
                    inputClassName="w-full"
                    value={validUntil}
                    onChange={(e) => setValidUntil((e.value as Date) ?? null)}
                    dateFormat="dd/mm/yy"
                    placeholder="No expiry"
                    showIcon
                    showButtonBar
                />
            </div>
        </Dialog>
    );
}
